import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../App';

// Size Ratio Mapping (ERP form 5401), matrix style. Pick a TAS → tick which sizes are present →
// a grid appears with those sizes as columns and each shade/design line as a row → type the qty in
// each box. Row totals, column totals and a grand total update live; one Save writes every cell to
// order_sizetable(_det) via /api/size-ratio/save-matrix (same tables as the PHP form).
const accent = 'var(--teal)';
const n = v => { const x = Number(v); return isFinite(x) ? x : 0; };

// ─────────────────────────────────────────────────────────────────────────────
// The matrix editor for one order.
function OrderMatrix({ orderid, tas_no, lines, sizeOptions, onSaved }) {
  // grid[refid][sizeid] = qty string
  const [grid, setGrid] = useState({});
  const [cols, setCols] = useState([]);        // selected size ids (columns), in sizeOptions order
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);        // { ok, text }

  // Seed from saved data whenever the loaded lines change (initial load + after a save reload).
  useEffect(() => {
    const g = {};
    const used = new Set();
    (lines || []).forEach(l => {
      g[l.refid] = {};
      (l.sizes || []).forEach(s => { g[l.refid][s.sizeid] = String(s.qty); used.add(s.sizeid); });
    });
    setGrid(g);
    // default columns = sizes already used; keep them in sizeOptions order
    setCols(sizeOptions.filter(o => used.has(o.id)).map(o => o.id));
    setMsg(null);
  }, [lines, sizeOptions]);

  const colSet = useMemo(() => new Set(cols), [cols]);
  const colSizes = useMemo(() => sizeOptions.filter(o => colSet.has(o.id)), [sizeOptions, colSet]);

  function toggleCol(id) {
    setMsg(null);
    setCols(prev => {
      if (prev.includes(id)) {
        // don't allow removing a column that has values entered
        const hasVal = (lines || []).some(l => n(grid[l.refid]?.[id]) > 0);
        if (hasVal) { setMsg({ ok: false, text: 'Clear that size’s quantities before removing the column' }); return prev; }
        return prev.filter(x => x !== id);
      }
      // keep sizeOptions order
      const next = new Set([...prev, id]);
      return sizeOptions.filter(o => next.has(o.id)).map(o => o.id);
    });
  }

  function setCell(refid, sizeid, val) {
    if (val !== '' && !/^\d*\.?\d*$/.test(val)) return;
    setMsg(null);
    setGrid(prev => ({ ...prev, [refid]: { ...(prev[refid] || {}), [sizeid]: val } }));
  }

  const rowTotal = refid => (colSizes.reduce((a, s) => a + n(grid[refid]?.[s.id]), 0));
  const colTotal = sizeid => (lines || []).reduce((a, l) => a + n(grid[l.refid]?.[sizeid]), 0);
  const grandTotal = (lines || []).reduce((a, l) => a + rowTotal(l.refid), 0);
  const grandOrdered = (lines || []).reduce((a, l) => a + n(l.pcs), 0);
  const overRows = (lines || []).filter(l => rowTotal(l.refid) > n(l.pcs)).map(l => l.refid);
  const canSave = overRows.length === 0 && grandTotal > 0;

  async function save() {
    if (overRows.length) { setMsg({ ok: false, text: 'Some lines exceed their ordered quantity' }); return; }
    const cells = [];
    (lines || []).forEach(l => colSizes.forEach(s => {
      const q = n(grid[l.refid]?.[s.id]);
      if (q > 0) cells.push({ refid: l.refid, sizeid: s.id, quantity: q });
    }));
    setSaving(true); setMsg(null);
    try {
      const r = await api('/size-ratio/save-matrix', { method: 'POST', body: JSON.stringify({ orderid, cells }) });
      if (r && r.status === 'ok') { setMsg({ ok: true, text: 'Saved ✓' }); onSaved(); }
      else setMsg({ ok: false, text: r?.message || 'Save failed' });
    } catch { setMsg({ ok: false, text: 'Save failed' }); }
    finally { setSaving(false); }
  }

  const th = { padding: '9px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textAlign: 'center', whiteSpace: 'nowrap', background: 'var(--bg3)', borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, zIndex: 2 };
  const stickyL = { position: 'sticky', left: 0, zIndex: 3, background: 'var(--bg3)', textAlign: 'left' };
  const td = { padding: '4px 6px', fontSize: 13, textAlign: 'center', borderBottom: '1px solid var(--border)' };
  const cellInput = over => ({ width: 62, padding: '7px 4px', textAlign: 'center', borderRadius: 6, border: `1px solid ${over ? 'var(--red)' : 'var(--border)'}`, background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, outline: 'none' });

  return (
    <div style={{ padding: '10px 14px 30px' }}>
      {/* ── size picker ── */}
      <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 8px' }}>
        1 · Which sizes are in this order?
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {sizeOptions.map(o => {
          const on = colSet.has(o.id);
          return (
            <button key={o.id} onClick={() => toggleCol(o.id)}
              style={{ padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${on ? accent : 'var(--border)'}`,
                background: on ? accent : 'var(--bg2)', color: on ? '#fff' : 'var(--text2)' }}>
              {o.name}
            </button>
          );
        })}
      </div>

      {colSizes.length === 0 ? (
        <div style={{ padding: 26, textAlign: 'center', color: 'var(--text3)', fontSize: 12, border: '1px dashed var(--border)', borderRadius: 10 }}>
          Tick the sizes above to build the grid.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '2px 0 8px' }}>
            2 · Enter the quantity for each size
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 360 }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...stickyL, minWidth: 150 }}>Shade / Design</th>
                  {colSizes.map(s => <th key={s.id} style={th}>{s.name}</th>)}
                  <th style={{ ...th, background: 'var(--bg2)' }}>Total</th>
                  <th style={th}>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {(lines || []).map(l => {
                  const rt = rowTotal(l.refid);
                  const ord = n(l.pcs);
                  const over = rt > ord;
                  const done = rt === ord && rt > 0;
                  const totColor = over ? 'var(--red)' : done ? 'var(--green)' : rt > 0 ? 'var(--yellow)' : 'var(--text3)';
                  return (
                    <tr key={l.refid}>
                      <td style={{ ...td, ...stickyL, background: 'var(--bg)', textAlign: 'left', padding: '6px 8px', borderRight: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{l.shade || '—'}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{l.design || '—'}</div>
                      </td>
                      {colSizes.map(s => (
                        <td key={s.id} style={td}>
                          <input inputMode="numeric" value={grid[l.refid]?.[s.id] ?? ''} onChange={e => setCell(l.refid, s.id, e.target.value)}
                            style={cellInput(over)} placeholder="0" />
                        </td>
                      ))}
                      <td style={{ ...td, fontWeight: 700, fontSize: 14, color: totColor, background: 'var(--bg2)' }}>{rt.toLocaleString()}</td>
                      <td style={{ ...td, color: 'var(--text3)', fontSize: 12 }}>{ord.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...td, ...stickyL, background: 'var(--bg3)', textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--text)', borderTop: '2px solid var(--border)' }}>Grand Total</td>
                  {colSizes.map(s => (
                    <td key={s.id} style={{ ...td, fontWeight: 700, color: 'var(--text2)', background: 'var(--bg3)', borderTop: '2px solid var(--border)' }}>{colTotal(s.id).toLocaleString()}</td>
                  ))}
                  <td style={{ ...td, fontWeight: 800, fontSize: 14, color: grandTotal > grandOrdered ? 'var(--red)' : accent, background: 'var(--bg3)', borderTop: '2px solid var(--border)' }}>{grandTotal.toLocaleString()}</td>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--text3)', background: 'var(--bg3)', borderTop: '2px solid var(--border)' }}>{grandOrdered.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <button onClick={save} disabled={!canSave || saving}
              style={{ background: canSave ? accent : 'var(--border)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: canSave && !saving ? 'pointer' : 'default', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save size ratio'}
            </button>
            {overRows.length > 0 && <span style={{ color: 'var(--red)', fontSize: 12, fontWeight: 600 }}>Some lines exceed the ordered quantity</span>}
            {msg && <span style={{ color: msg.ok ? 'var(--green)' : 'var(--red)', fontSize: 13, fontWeight: 600 }}>{msg.text}</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
            Saving replaces the full size breakdown for these lines. A row total may be less than ordered (partial) but never more.
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
const FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'done', label: 'Done' },
  { key: 'all', label: 'All' },
];

export default function SizeRatioView({ setView, Header, embedded }) {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);        // { orderid, tas_no }
  const [detail, setDetail] = useState(null);  // { lines, sizeOptions }
  const [loading, setLoading] = useState(false);

  const loadOrders = useCallback((f) => {
    setOrders(null);
    api(`/size-ratio/orders?filter=${f}`).then(d => setOrders(Array.isArray(d) ? d : [])).catch(() => setOrders([]));
  }, []);

  useEffect(() => { if (!sel) loadOrders(filter); }, [filter, sel, loadOrders]);

  const loadDetail = useCallback((orderid) => {
    setLoading(true);
    api(`/size-ratio/${orderid}`).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  function openOrder(o) { setSel(o); setDetail(null); loadDetail(o.orderid); }
  function backToList() { setSel(null); setDetail(null); }   // useEffect reloads the list

  // ── Order detail (matrix) ──
  if (sel) {
    const lines = detail?.lines || [];
    const totalPcs = lines.reduce((a, l) => a + n(l.pcs), 0);
    const totalMapped = lines.reduce((a, l) => a + n(l.mapped), 0);
    return (
      <div className="screen">
        <Header subtitle="SIZE RATIO" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={backToList} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>{sel.tas_no}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{totalMapped.toLocaleString()} / {totalPcs.toLocaleString()} pcs mapped</div>
        </div>
        {/* TAS header card — same fields as the ERP form 5401 top strip */}
        {detail && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, padding: '10px 16px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
            {[
              ['Buyer', detail.buyer],
              ['Merchandiser', detail.merchandiser],
              ['TAS Date', detail.tas_date],
              ['Entry Date', detail.entry_dt],
            ].map(([k, v]) => (
              <div key={k} style={{ flex: '1 1 130px', minWidth: 120, padding: '2px 8px' }}>
                <div style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text3)' }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{v || '—'}</div>
              </div>
            ))}
          </div>
        )}
        {loading || !detail ? <div className="loading">LOADING…</div>
          : lines.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No order lines</div>
          : <OrderMatrix orderid={sel.orderid} tas_no={sel.tas_no} lines={lines} sizeOptions={detail.sizeOptions || []} onSaved={() => loadDetail(sel.orderid)} />}
      </div>
    );
  }

  // ── Order picker ──
  const list = (orders || []).filter(o => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return String(o.tas_no || '').toLowerCase().includes(s) || String(o.buyer || '').toLowerCase().includes(s);
  });
  return (
    <div className="screen">
      <Header subtitle="SIZE RATIO" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        {!embedded && <button onClick={() => setView('tas-daily')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>}
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Size Ratio Mapping</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{(orders || []).length} {filter === 'all' ? 'orders' : filter}</div>
      </div>
      {/* Done / Pending / All */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 16px 4px' }}>
        {FILTERS.map(f => {
          const on = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{ flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${on ? accent : 'var(--border)'}`,
                background: on ? accent : 'var(--bg2)', color: on ? '#fff' : 'var(--text2)' }}>
              {f.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '8px 16px 4px' }}>
        <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search TAS or buyer…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ padding: '8px 16px 28px' }}>
        {orders === null ? <div className="loading">LOADING…</div>
          : list.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>{filter === 'done' ? 'No fully-mapped orders yet.' : filter === 'all' ? 'No orders found.' : 'Nothing pending — all size ratios mapped.'}</div>
          : list.map(o => {
            const done = n(o.pending_pcs) <= 0;
            return (
            <div key={o.orderid} onClick={() => openOrder(o)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${done ? 'var(--green)' : accent}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{o.tas_no}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{o.buyer || '—'}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text3)' }}>
                {done
                  ? <><div style={{ color: 'var(--green)', fontWeight: 700 }}>✓ fully mapped</div><div>{Number(o.totpcs || 0).toLocaleString()} pcs</div></>
                  : <><div style={{ color: 'var(--yellow)', fontWeight: 700 }}>{o.pending_lines} line{o.pending_lines > 1 ? 's' : ''} pending</div><div>{Number(o.pending_pcs || 0).toLocaleString()} pcs left</div></>}
              </div>
            </div>
          ); })}
      </div>
    </div>
  );
}
